import { AlertCircle, Loader2, Plus, Search } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import manifestService, {
  AWBSearchResult,
} from '../../services/manifestService';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';

// Custom debounce hook
function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  ) as T;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFn;
}

interface AWBSearchProps {
  onAddAWBs: (awbNumbers: string[]) => Promise<void>;
  disabled?: boolean;
}

const AWBSearch: React.FC<AWBSearchProps> = ({
  onAddAWBs,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<AWBSearchResult[]>([]);
  const [selectedAWBs, setSelectedAWBs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const { toast } = useToast();

  // Search function
  const performSearch = useCallback(async (term: string) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const results = await manifestService.searchAWBs(term);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching AWBs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useDebounce(performSearch, 300);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const handleSelectAWB = (awbNumber: string) => {
    setSelectedAWBs(prev =>
      prev.includes(awbNumber)
        ? prev.filter(a => a !== awbNumber)
        : [...prev, awbNumber]
    );
  };

  const handleSelectAll = () => {
    if (selectedAWBs.length === searchResults.length) {
      setSelectedAWBs([]);
    } else {
      setSelectedAWBs(searchResults.map(r => r.awbNumber));
    }
  };

  const handleAddSelected = async () => {
    if (selectedAWBs.length === 0) {
      toast({
        title: 'No AWBs Selected',
        description: 'Please select at least one AWB to add',
        variant: 'destructive',
      });
      return;
    }

    setAdding(true);
    try {
      await onAddAWBs(selectedAWBs);
      setSelectedAWBs([]);
      setSearchTerm('');
      setSearchResults([]);
      toast({
        title: 'Success',
        description: `Added ${selectedAWBs.length} AWB(s) to manifest`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add AWBs',
        variant: 'destructive',
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className='space-y-4'>
      {/* Search Input */}
      <div className='flex flex-col sm:flex-row gap-2'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder='Search AWB or forwarding number...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className='pl-9'
            disabled={disabled}
          />
        </div>
        <Button
          onClick={handleAddSelected}
          disabled={disabled || selectedAWBs.length === 0 || adding}
          className='bg-green-600 hover:bg-green-700 w-full sm:w-auto'
        >
          {adding ? (
            <Loader2 className='h-4 w-4 animate-spin mr-2' />
          ) : (
            <Plus className='h-4 w-4 mr-2' />
          )}
          Add Selected ({selectedAWBs.length})
        </Button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className='flex items-center justify-center py-4'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          <span className='ml-2 text-muted-foreground'>Searching...</span>
        </div>
      )}

      {/* Search Results */}
      {!loading && searchResults.length > 0 && (
        <div className='border rounded-md overflow-hidden'>
          <div className='bg-muted/50 p-2 flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Checkbox
                checked={selectedAWBs.length === searchResults.length}
                onCheckedChange={handleSelectAll}
              />
              <span className='text-sm font-medium'>
                {searchResults.length} result(s) found
              </span>
            </div>
            {selectedAWBs.length > 0 && (
              <Badge variant='secondary'>{selectedAWBs.length} selected</Badge>
            )}
          </div>
          <div className='max-h-64 overflow-y-auto'>
            {searchResults.map(awb => (
              <div
                key={awb.awbNumber}
                className={`flex items-center gap-4 p-3 border-b last:border-b-0 hover:bg-muted/30 cursor-pointer ${
                  selectedAWBs.includes(awb.awbNumber) ? 'bg-primary/5' : ''
                }`}
                onClick={() => handleSelectAWB(awb.awbNumber)}
              >
                <Checkbox
                  checked={selectedAWBs.includes(awb.awbNumber)}
                  onCheckedChange={() => handleSelectAWB(awb.awbNumber)}
                />
                <div className='flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 text-sm'>
                  <div>
                    <div className='font-medium'>{awb.awbNumber}</div>
                    <div className='text-muted-foreground text-xs'>AWB No.</div>
                  </div>
                  <div>
                    <div>{awb.forwardingNumber || '-'}</div>
                    <div className='text-muted-foreground text-xs'>
                      Forwarding No.
                    </div>
                  </div>
                  <div>
                    <div>{awb.weight} kg</div>
                    <div className='text-muted-foreground text-xs'>Weight</div>
                  </div>
                  <div>
                    <div>{awb.pcs}</div>
                    <div className='text-muted-foreground text-xs'>Pcs</div>
                  </div>
                  <div>
                    <Badge variant='outline'>{awb.currentStatus}</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {!loading && searchTerm.length >= 2 && searchResults.length === 0 && (
        <div className='flex flex-col items-center justify-center py-8 text-muted-foreground'>
          <AlertCircle className='h-8 w-8 mb-2' />
          <p>No AWBs found matching "{searchTerm}"</p>
          <p className='text-sm'>Try a different search term</p>
        </div>
      )}
    </div>
  );
};

export default AWBSearch;
